import type {
  OtherIncomeClaimDraft,
  OtherIncomeClaimEntry,
  OtherIncomeEarnerSnapshot,
  OtherIncomeEarnerStep,
  YesNo,
} from './otherIncomeEarnerTypes';
import { MEMBER_LOOP_STEPS, OTHER_INCOME_EARNER_STEP_ORDER, MAX_OTHER_INCOME_CLAIM_ENTRIES } from './otherIncomeEarnerTypes';

function nextAfterMemberLoop(
  snap: OtherIncomeEarnerSnapshot,
  draft: OtherIncomeClaimDraft
): OtherIncomeEarnerStep {
  const completedCount = snap.entries.length + (draft.empFamilyId > 0 ? 1 : 0);
  return completedCount >= MAX_OTHER_INCOME_CLAIM_ENTRIES ? 'review' : 'addAnother';
}

export function stepQuestionKey(step: OtherIncomeEarnerStep, taxYear: number): string {
  if (step === 'changedDuringYear') {
    return `submitDocuments.dependentDeduction.otherIncomeEarner.steps.changedDuringYear`;
  }
  return `submitDocuments.dependentDeduction.otherIncomeEarner.steps.${step}`;
}

export function changedDuringYearQuestion(t: (k: string) => string, taxYear: number): string {
  return t('submitDocuments.dependentDeduction.otherIncomeEarner.steps.changedDuringYear').replace(
    '{{taxYear}}',
    String(taxYear)
  );
}

export function stepIndex(step: OtherIncomeEarnerStep): number {
  return OTHER_INCOME_EARNER_STEP_ORDER.indexOf(step);
}

export function isMemberLoopStep(step: OtherIncomeEarnerStep): boolean {
  return MEMBER_LOOP_STEPS.includes(step);
}

export function isSkippedStep(step: OtherIncomeEarnerStep, draft: OtherIncomeClaimDraft): boolean {
  if (step === 'earnerRoleOther') return draft.earnerRole !== 'other';
  if (step === 'changeReasonOther') {
    return draft.changedDuringYear !== 'yes' || draft.changeReason !== 'other';
  }
  if (step === 'changeDate' || step === 'changeReason') {
    return draft.changedDuringYear !== 'yes';
  }
  return false;
}

/** Last visible answered step for a saved member entry (used for review-mode undo). */
export function lastAnsweredStepForEntry(entry: OtherIncomeClaimEntry): OtherIncomeEarnerStep {
  for (let i = MEMBER_LOOP_STEPS.length - 1; i >= 0; i--) {
    const s = MEMBER_LOOP_STEPS[i];
    if (isSkippedStep(s, entry)) continue;
    if (isDraftStepAnswered(s, entry)) return s;
  }
  return 'pickMember';
}

export function nextStepAfter(
  current: OtherIncomeEarnerStep,
  snap: OtherIncomeEarnerSnapshot,
  draft: OtherIncomeClaimDraft
): OtherIncomeEarnerStep | null {
  switch (current) {
    case 'intro':
      return 'anyClaimedGate';
    case 'anyClaimedGate':
      return snap.anyClaimedByOther === 'yes' ? 'pickMember' : 'review';
    case 'pickMember':
      return 'earnerRole';
    case 'earnerRole':
      return draft.earnerRole === 'other' ? 'earnerRoleOther' : 'earnerName';
    case 'earnerRoleOther':
      return 'earnerName';
    case 'earnerName':
      return 'earnerAddress';
    case 'earnerAddress':
      return 'memberToEarnerRelationship';
    case 'memberToEarnerRelationship':
      return 'changedDuringYear';
    case 'changedDuringYear':
      return draft.changedDuringYear === 'yes' ? 'changeDate' : nextAfterMemberLoop(snap, draft);
    case 'changeDate':
      return 'changeReason';
    case 'changeReason':
      return draft.changeReason === 'other' ? 'changeReasonOther' : nextAfterMemberLoop(snap, draft);
    case 'changeReasonOther':
      return nextAfterMemberLoop(snap, draft);
    case 'addAnother':
      return 'review';
    case 'review':
      return null;
    default:
      return null;
  }
}

export function nextVisibleStep(
  current: OtherIncomeEarnerStep,
  snap: OtherIncomeEarnerSnapshot,
  draft: OtherIncomeClaimDraft
): OtherIncomeEarnerStep | null {
  let next = nextStepAfter(current, snap, draft);
  while (next && isSkippedStep(next, draft)) {
    next = nextStepAfter(next, snap, draft);
  }
  return next;
}

export function prevStepForUndo(
  current: OtherIncomeEarnerStep,
  draft: OtherIncomeClaimDraft
): OtherIncomeEarnerStep | null {
  const idx = stepIndex(current);
  if (idx <= 0) return null;
  for (let i = idx - 1; i >= 0; i--) {
    const candidate = OTHER_INCOME_EARNER_STEP_ORDER[i];
    if (candidate === 'review' || candidate === 'intro') continue;
    if (isSkippedStep(candidate, draft)) continue;
    return candidate;
  }
  return null;
}

export function undoSourceStepForCurrent(
  current: OtherIncomeEarnerStep,
  draft: OtherIncomeClaimDraft,
  reviewMode: boolean
): OtherIncomeEarnerStep | null {
  if (current === 'review') {
    return draft.empFamilyId > 0 ? prevStepForUndo(current, draft) : null;
  }
  if (current === 'intro') return null;
  return prevStepForUndo(current, draft);
}

export function undoTargetStep(
  current: OtherIncomeEarnerStep,
  draft: OtherIncomeClaimDraft,
  snap: OtherIncomeEarnerSnapshot,
  committed: Set<OtherIncomeEarnerStep>,
  reviewMode: boolean
): OtherIncomeEarnerStep | null {
  if (reviewMode) {
    if (committed.has('addAnother') && snap.entries.length > 0) return 'addAnother';
    if (snap.entries.length > 0) {
      return lastAnsweredStepForEntry(snap.entries[snap.entries.length - 1]);
    }
    if (committed.has('anyClaimedGate') && snap.anyClaimedByOther !== '') return 'anyClaimedGate';
    return null;
  }
  if (current === 'intro') return null;

  const curIdx = stepIndex(current);
  let best: OtherIncomeEarnerStep | null = null;
  let bestIdx = -1;
  for (const s of OTHER_INCOME_EARNER_STEP_ORDER) {
    if (s === 'intro' || s === 'review') continue;
    if (!committed.has(s)) continue;
    if (isSkippedStep(s, draft)) continue;
    const idx = stepIndex(s);
    if (idx < curIdx && idx > bestIdx) {
      bestIdx = idx;
      best = s;
    }
  }
  return best;
}

export function showUndoForStep(
  step: OtherIncomeEarnerStep,
  current: OtherIncomeEarnerStep,
  draft: OtherIncomeClaimDraft,
  snap: OtherIncomeEarnerSnapshot,
  committed: boolean,
  reviewMode: boolean,
  allCommitted: Set<OtherIncomeEarnerStep>
): boolean {
  if (step === 'intro') return false;
  const target = undoTargetStep(current, draft, snap, allCommitted, reviewMode);
  if (target !== step) return false;
  if (reviewMode) {
    if (step === 'anyClaimedGate' || step === 'addAnother') return committed;
    if (snap.entries.length === 0) return false;
    return lastAnsweredStepForEntry(snap.entries[snap.entries.length - 1]) === step;
  }
  return committed;
}

export function landOnStepAfterUndo(
  undoFrom: OtherIncomeEarnerStep,
  draft: OtherIncomeClaimDraft
): OtherIncomeEarnerStep | null {
  if (undoFrom === 'addAnother') return prevStepForUndo('addAnother', draft) ?? 'pickMember';
  return undoFrom;
}

export function draftFieldForStep(step: OtherIncomeEarnerStep): (keyof OtherIncomeClaimDraft)[] {
  switch (step) {
    case 'pickMember':
      return ['empFamilyId', 'memberFullName', 'memberRelationship'];
    case 'earnerRole':
      return ['earnerRole'];
    case 'earnerRoleOther':
      return ['earnerRoleOther'];
    case 'earnerName':
      return ['earnerName'];
    case 'earnerAddress':
      return ['earnerAddress'];
    case 'memberToEarnerRelationship':
      return ['memberToEarnerRelationship'];
    case 'changedDuringYear':
      return ['changedDuringYear'];
    case 'changeDate':
      return ['changeDate'];
    case 'changeReason':
      return ['changeReason'];
    case 'changeReasonOther':
      return ['changeReasonOther'];
    default:
      return [];
  }
}

export function clearDraftFromStep(step: OtherIncomeEarnerStep): Partial<OtherIncomeClaimDraft> {
  const patch: Partial<OtherIncomeClaimDraft> = {};
  const idx = stepIndex(step);
  for (const s of OTHER_INCOME_EARNER_STEP_ORDER) {
    if (stepIndex(s) < idx) continue;
    for (const field of draftFieldForStep(s)) {
      if (field === 'empFamilyId') (patch as Record<string, unknown>)[field] = 0;
      else if (field === 'memberFullName' || field === 'memberRelationship' || field === 'earnerRoleOther' || field === 'earnerName' || field === 'earnerAddress' || field === 'changeDate' || field === 'changeReasonOther')
        (patch as Record<string, unknown>)[field] = '';
      else (patch as Record<string, unknown>)[field] = '';
    }
  }
  return patch;
}

export function isDraftStepAnswered(step: OtherIncomeEarnerStep, draft: OtherIncomeClaimDraft): boolean {
  switch (step) {
    case 'pickMember':
      return draft.empFamilyId > 0;
    case 'earnerRole':
      return draft.earnerRole !== '';
    case 'earnerRoleOther':
      return draft.earnerRoleOther.trim() !== '';
    case 'earnerName':
      return draft.earnerName.trim() !== '';
    case 'earnerAddress':
      return draft.earnerAddress.trim() !== '';
    case 'memberToEarnerRelationship':
      return draft.memberToEarnerRelationship !== '';
    case 'changedDuringYear':
      return draft.changedDuringYear === 'yes' || draft.changedDuringYear === 'no';
    case 'changeDate':
      return draft.changeDate.trim() !== '';
    case 'changeReason':
      return draft.changeReason !== '';
    case 'changeReasonOther':
      return draft.changeReasonOther.trim() !== '';
    default:
      return false;
  }
}

export function entryFromDraft(draft: OtherIncomeClaimDraft): OtherIncomeClaimEntry {
  return { ...draft };
}

export function committedStepsForReview(
  snap: OtherIncomeEarnerSnapshot,
  cursor: OtherIncomeEarnerStep | null
): Set<OtherIncomeEarnerStep> {
  const comm = new Set<OtherIncomeEarnerStep>(['intro']);
  if (snap.anyClaimedByOther !== '') comm.add('anyClaimedGate');
  if (cursor === 'addAnother') comm.add('addAnother');
  else if (snap.entries.length >= MAX_OTHER_INCOME_CLAIM_ENTRIES) comm.add('addAnother');
  return comm;
}

export function addAnotherAnswerForReview(
  snap: OtherIncomeEarnerSnapshot,
  cursor: OtherIncomeEarnerStep | null
): YesNo | '' {
  if (cursor === 'addAnother') return 'no';
  if (snap.entries.length >= MAX_OTHER_INCOME_CLAIM_ENTRIES) return 'yes';
  return '';
}

export function otherIncomeEarnerCursor(step: OtherIncomeEarnerStep): string {
  return `otherIncomeEarner:${step}`;
}

export function parseOtherIncomeEarnerCursor(id: string | null | undefined): OtherIncomeEarnerStep | null {
  const m = (id ?? '').match(/^otherIncomeEarner:(.+)$/);
  if (!m) return null;
  const step = m[1] as OtherIncomeEarnerStep;
  return OTHER_INCOME_EARNER_STEP_ORDER.includes(step) ? step : null;
}

export function buildResumeStep(
  snap: OtherIncomeEarnerSnapshot,
  draft: OtherIncomeClaimDraft,
  sectionDone: boolean
): OtherIncomeEarnerStep {
  if (sectionDone && snap.anyClaimedByOther !== '') return 'review';
  if (snap.anyClaimedByOther === '') return 'intro';
  if (snap.anyClaimedByOther === 'no') return 'review';
  for (const s of MEMBER_LOOP_STEPS) {
    if (isSkippedStep(s, draft)) continue;
    if (!isDraftStepAnswered(s, draft) && draft.empFamilyId > 0) return s;
  }
  if (draft.empFamilyId === 0) return 'pickMember';
  return 'addAnother';
}
